import React, { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Card } from './AdminPanelPrimitives';
import { STATUS_PALETTE } from './adminDesignSystem';

const MAP_STYLE_URL = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

// At this many items the DOM-marker approach (one <button> per pin with
// hover popups) becomes laggy and visually impossible to use. Above
// this we switch to MapLibre's GeoJSON source-based clustering: the
// hover/popup affordance is sacrificed because at 1000+ pins it's
// useless anyway — admins zoom into clusters first, then individual
// pins re-emerge as singles below the threshold zoom.
const CLUSTER_THRESHOLD = 120;

const CLUSTER_SOURCE_ID = 'admin-cluster-source';
const CLUSTER_LAYER_ID = 'admin-cluster-circles';
const CLUSTER_COUNT_LAYER_ID = 'admin-cluster-count';
const CLUSTER_POINTS_LAYER_ID = 'admin-cluster-points';

const createMarkerElement = (color, active) => {
  // MapLibre positions the marker via `transform: translate(x, y)` on this
  // outer element. If we write to `element.style.transform` directly we
  // CLOBBER that translate and the marker snaps to (0,0) — the top-left
  // corner of the map. So we wrap the visual in an inner span and apply
  // the hover scale to the SPAN, not the outer button.
  const element = document.createElement('button');
  element.type = 'button';
  element.setAttribute('aria-label', 'Map marker');
  element.style.padding = '0';
  element.style.border = 'none';
  element.style.background = 'transparent';
  element.style.cursor = 'pointer';

  const dot = document.createElement('span');
  dot.style.display = 'block';
  dot.style.width = active ? '22px' : '16px';
  dot.style.height = active ? '22px' : '16px';
  dot.style.borderRadius = '50%';
  dot.style.border = '2px solid #FFFFFF';
  dot.style.background = color;
  dot.style.boxShadow = active ? `0 0 0 7px ${color}30, 0 8px 18px rgba(17,17,17,0.18)` : '0 6px 14px rgba(17,17,17,0.14)';
  dot.style.transition = 'transform 140ms ease';
  dot.style.transformOrigin = 'center';
  element.appendChild(dot);

  element.addEventListener('mouseenter', () => { dot.style.transform = 'scale(1.18)'; });
  element.addEventListener('mouseleave', () => { dot.style.transform = 'scale(1)'; });
  return element;
};

const escapeHtml = (value) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const buildPopupHtml = (item) => {
  const palette = STATUS_PALETTE[item.statusTone] || STATUS_PALETTE.neutral;
  const ownerLine = item.ownerName ? `<div style="font-size:12px;color:#3B404B;margin-top:8px;font-weight:600;">${escapeHtml(item.ownerName)}</div>` : '';
  const emailLine = item.ownerEmail ? `<div style="font-size:11px;color:#6B7280;margin-top:2px;">${escapeHtml(item.ownerEmail)}</div>` : '';
  return `
    <div style="font-family:'Manrope',sans-serif;min-width:200px;max-width:260px;">
      <div style="display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:9999px;background:${palette.soft};color:${palette.text};font-size:10.5px;font-weight:800;letter-spacing:0.4px;text-transform:uppercase;border:1px solid ${palette.solid}33;">
        <span style="width:7px;height:7px;border-radius:9999px;background:${palette.solid};"></span>
        ${escapeHtml(item.badgeText || palette.label)}
      </div>
      <div style="margin-top:8px;font-size:14px;font-weight:700;color:#0F1115;line-height:1.25;">${escapeHtml(item.title)}</div>
      <div style="font-size:12px;color:#6B7280;margin-top:3px;">${escapeHtml(item.subtitle || '')}</div>
      ${ownerLine}${emailLine}
    </div>
  `;
};

const buildClusterGeoJson = (items) => ({
  type: 'FeatureCollection',
  features: items.map((item) => ({
    type: 'Feature',
    properties: {
      id: item.id,
      title: item.title || '',
      subtitle: item.subtitle || '',
      markerColor: item.markerColor || '#2F79EA',
    },
    geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
  })),
});

const ensureClusterLayers = (map) => {
  if (map.getSource(CLUSTER_SOURCE_ID)) return;

  map.addSource(CLUSTER_SOURCE_ID, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: true,
    clusterRadius: 48,
    clusterMaxZoom: 11,
  });

  // Cluster bubbles — colour and radius scale with the point count so a
  // dense city stands out from a single venue.
  map.addLayer({
    id: CLUSTER_LAYER_ID,
    type: 'circle',
    source: CLUSTER_SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step', ['get', 'point_count'],
        '#5BC0EB', 10,   // 1-9   small clusters: cool blue
        '#2F79EA', 50,   // 10-49 medium
        '#7036F2', 200,  // 50-199 large purple
        '#DD0608',       // 200+  very large red
      ],
      'circle-radius': [
        'step', ['get', 'point_count'],
        16, 10, 22, 50, 28, 200, 34,
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
      'circle-opacity': 0.92,
    },
  });

  // Numeric label sitting inside the cluster bubble.
  map.addLayer({
    id: CLUSTER_COUNT_LAYER_ID,
    type: 'symbol',
    source: CLUSTER_SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-size': 13,
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#FFFFFF',
    },
  });

  // Individual pin dots (revealed once a cluster is broken open).
  map.addLayer({
    id: CLUSTER_POINTS_LAYER_ID,
    type: 'circle',
    source: CLUSTER_SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': ['coalesce', ['get', 'markerColor'], '#2F79EA'],
      'circle-radius': 7,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
    },
  });
};

const removeClusterLayers = (map) => {
  [CLUSTER_COUNT_LAYER_ID, CLUSTER_LAYER_ID, CLUSTER_POINTS_LAYER_ID].forEach((id) => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  if (map.getSource(CLUSTER_SOURCE_ID)) map.removeSource(CLUSTER_SOURCE_ID);
};

export const AdminGeoMapCanvas = ({ autoSelectFirst = true, dataTestId, emptyCopy, height = 620, items, onSelect, selectedId, sticky = true, title }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupsRef = useRef([]);
  const lastCenteredIdRef = useRef(null);
  const clusterHandlersRef = useRef(null);
  // Tracks which items signature is currently laid down. Prevents the
  // marker rebuild effect from calling fitBounds on every selectedId
  // change (the cause of the "map unzooms on every pin click" bug).
  const lastItemsSignatureRef = useRef('');

  const useClustering = items.length > CLUSTER_THRESHOLD;

  const selectedItem = useMemo(() => {
    if (selectedId) {
      return items.find((item) => item.id === selectedId) || (autoSelectFirst ? items[0] || null : null);
    }
    return autoSelectFirst ? items[0] || null : null;
  }, [autoSelectFirst, items, selectedId]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: [10, 20],
      zoom: 1.6,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    // Dismiss popups on map background click (covers stuck-popup edge
    // cases when a mouseleave gets dropped on fast pin-to-pin movement).
    map.on('click', () => {
      popupsRef.current.forEach((popup) => {
        try { popup.remove(); } catch { /* already removed */ }
      });
    });
    mapRef.current = map;

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      popupsRef.current.forEach((popup) => {
        try { popup.remove(); } catch { /* already removed */ }
      });
      popupsRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Always tear down the previous render's markers/popups/cluster
    // handlers before we lay down the next one. This stops orphaned
    // DOM markers from a 50-pin render hanging around when the user
    // jumps to a 2000-pin map.
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    popupsRef.current.forEach((popup) => {
      try { popup.remove(); } catch { /* already removed */ }
    });
    popupsRef.current = [];
    if (clusterHandlersRef.current) {
      const { handlers } = clusterHandlersRef.current;
      handlers.forEach(({ event, layer, handler }) => {
        try { map.off(event, layer, handler); } catch { /* not bound */ }
      });
      clusterHandlersRef.current = null;
    }

    if (!items.length) {
      try { removeClusterLayers(map); } catch { /* style not ready */ }
      lastItemsSignatureRef.current = '';
      return;
    }

    // Refit bounds ONLY when the items array actually changes — not on
    // every selectedId change (which also triggers this effect). Without
    // this guard, every pin click resets the zoom level.
    const itemsSignature = items.map((item) => item.id).join('|');
    const itemsChanged = itemsSignature !== lastItemsSignatureRef.current;
    if (itemsChanged) {
      lastItemsSignatureRef.current = itemsSignature;
    }

    if (useClustering) {
      // Heavy path — 1000+ pins.
      try { removeClusterLayers(map); } catch { /* nothing to remove */ }

      const applyClusterLayers = () => {
        ensureClusterLayers(map);
        const source = map.getSource(CLUSTER_SOURCE_ID);
        if (source) source.setData(buildClusterGeoJson(items));
      };

      if (map.isStyleLoaded()) applyClusterLayers();
      else map.once('load', applyClusterLayers);

      const clusterClickHandler = (event) => {
        const features = map.queryRenderedFeatures(event.point, { layers: [CLUSTER_LAYER_ID] });
        const clusterId = features[0]?.properties?.cluster_id;
        if (clusterId == null) return;
        const source = map.getSource(CLUSTER_SOURCE_ID);
        if (!source) return;
        source.getClusterExpansionZoom(clusterId, (error, zoom) => {
          if (error) return;
          map.easeTo({ center: features[0].geometry.coordinates, zoom: Math.min(zoom + 0.2, 14), duration: 480 });
        });
      };

      const pointClickHandler = (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        const id = feature.properties.id;
        if (id && typeof onSelect === 'function') onSelect(id);
      };

      const cursorEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
      const cursorLeave = () => { map.getCanvas().style.cursor = ''; };

      const handlers = [
        { event: 'click', layer: CLUSTER_LAYER_ID, handler: clusterClickHandler },
        { event: 'click', layer: CLUSTER_POINTS_LAYER_ID, handler: pointClickHandler },
        { event: 'mouseenter', layer: CLUSTER_LAYER_ID, handler: cursorEnter },
        { event: 'mouseleave', layer: CLUSTER_LAYER_ID, handler: cursorLeave },
        { event: 'mouseenter', layer: CLUSTER_POINTS_LAYER_ID, handler: cursorEnter },
        { event: 'mouseleave', layer: CLUSTER_POINTS_LAYER_ID, handler: cursorLeave },
      ];
      handlers.forEach(({ event, layer, handler }) => map.on(event, layer, handler));
      clusterHandlersRef.current = { handlers };
    } else {
      // Light path — DOM markers with rich hover popups.
      try { removeClusterLayers(map); } catch { /* style not ready */ }

      const bounds = new maplibregl.LngLatBounds();
      items.forEach((item) => {
        bounds.extend([item.longitude, item.latitude]);
        const element = createMarkerElement(item.markerColor, item.id === selectedId);
        const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, offset: 14, anchor: 'bottom', maxWidth: '260px', className: 'admin-map-popup' }).setHTML(buildPopupHtml(item));
        popupsRef.current.push(popup);
        const closeAllPopups = () => {
          popupsRef.current.forEach((other) => {
            try { other.remove(); } catch { /* already removed */ }
          });
        };
        // Click-only popup — hovering used to auto-open the bubble and
        // overlap nearby pins / radius highlights. Click selects the pin
        // AND opens its popup; the popup has a close button + clicking
        // map background dismisses it.
        element.addEventListener('click', (event) => {
          event.stopPropagation();
          closeAllPopups();
          if (typeof onSelect === 'function') onSelect(item.id);
          popup.setLngLat([item.longitude, item.latitude]).addTo(map);
        });
        const marker = new maplibregl.Marker({ element, anchor: 'center' })
          .setLngLat([item.longitude, item.latitude])
          .addTo(map);
        markersRef.current.push(marker);
      });

      if (items.length === 1) {
        if (itemsChanged) {
          map.easeTo({ center: [items[0].longitude, items[0].latitude], zoom: 5.4, duration: 600 });
        }
        return;
      }

      if (itemsChanged) {
        map.fitBounds(bounds, { padding: 54, duration: 650, maxZoom: 6 });
      }
      return;
    }

    // Cluster path: also fit bounds so the whole dataset is visible
    // when the admin first lands on the tab.
    const bounds = new maplibregl.LngLatBounds();
    items.forEach((item) => bounds.extend([item.longitude, item.latitude]));
    if (!bounds.isEmpty() && itemsChanged) {
      map.fitBounds(bounds, { padding: 54, duration: 650, maxZoom: 6 });
    }
  }, [items, onSelect, selectedId, useClustering]);

  useEffect(() => {
    if (!mapRef.current || !selectedItem) {
      return;
    }
    // Only recenter when the *selected id* actually changes — not on every
    // re-render where `selectedItem` becomes a new object reference. This
    // prevents the recenter easeTo from racing with the fitBounds animation
    // on first mount (the cause of the "pin lands in the corner" glitch).
    if (lastCenteredIdRef.current === selectedItem.id) {
      return;
    }
    lastCenteredIdRef.current = selectedItem.id;
    mapRef.current.easeTo({ center: [selectedItem.longitude, selectedItem.latitude], zoom: Math.max(mapRef.current.getZoom(), 4.6), duration: 420 });
  }, [selectedItem]);

  return (
    <Card style={sticky ? { position: 'sticky', top: 16 } : undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700 }}>{title}</p>
        {useClustering ? (
          <span
            data-testid="admin-geo-map-cluster-mode"
            title={`Showing ${items.length} pins. Clusters auto-expand as you zoom in.`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 9999, background: 'rgba(112, 63, 246, 0.10)', color: '#2F145F', fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}
          >
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 9999, background: '#7036F2' }} />
            CLUSTERED · {items.length.toLocaleString()} pins
          </span>
        ) : null}
      </div>
      {/* Sub-header copy: "Focused on X" when something is selected,
          "N pins on the map" when items exist but nothing is focused,
          the supplied emptyCopy ONLY when there really are zero items.
          Previously the empty copy showed whenever `autoSelectFirst=false`
          even with pins present, which made admins think the map was empty. */}
      {selectedItem ? (
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#666' }}>Focused on: {selectedItem.title}</p>
      ) : items.length > 0 ? (
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#666' }}>
          {items.length.toLocaleString()} pin{items.length === 1 ? '' : 's'} on the map. Hover a pin for details.
        </p>
      ) : (
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#666' }}>{emptyCopy}</p>
      )}
      <div ref={mapContainerRef} data-testid={dataTestId} style={{ width: '100%', height, borderRadius: 18, overflow: 'hidden', border: '1px solid #E7E7E7', marginTop: 14 }} />
    </Card>
  );
};

export default AdminGeoMapCanvas;
